/**
 * Sales Orders — Phase 5a implementation.
 *
 * Page composition:
 *   - PageHeader + "New order" action
 *   - KPI strip (Revenue MTD / Open / Awaiting approval / Avg order value)
 *   - Tab strip (status filter — All + every status enum)
 *   - DataTable
 *   - Detail Sheet on row click (header summary + dealer card + lines table
 *     + StatusTimeline + OrderActionsBar)
 *
 * Mock-aware: when `useMockData()` is true, reads come from
 * `salesMock`. Real wiring would use InstantDB queries.
 */

import * as React from "react";
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Receipt } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatRelative } from "@bizop/shared/dates";
import {
  canTransitionSalesOrder,
  type SalesOrderStatus,
} from "@bizop/shared/types";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { useCurrentRole } from "../../../lib/permissions";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { SalesOrderStatusBadge } from "../../../components/sales/SalesOrderStatusBadge";
import { StatusTimeline } from "../../../components/sales/StatusTimeline";
import {
  OrderActionsBar,
  type OrderAction,
} from "../../../components/sales/OrderActionsBar";
import { OrderRowActions } from "../../../components/sales/OrderRowActions";
import {
  SalesOrderForm,
  type SalesOrderFormValues,
} from "../../../components/forms/SalesOrderForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  findDealer,
  historyForOrder,
  linesForOrder,
  mockDealers,
  mockDealerTiers,
  mockSalesOrders,
  offersForDealer,
  type MockDealer,
  type MockDealerTier,
  type MockSalesOrder,
  type MockSalesOrderLine,
} from "../../../lib/salesMock";
import {
  mockProducts,
  mockTaxRates,
  type MockProduct,
  type MockTaxRate,
} from "../../../lib/catalogMock";
import {
  dealerOutstandingBaseCents,
  mockInvoices,
  type MockInvoice,
} from "../../../lib/invoicingMock";

import { mockBranches } from "../../../lib/inventoryMock";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  AttachmentBadge,
  AttachmentList,
} from "../../../components/attachments";

const SALES_ORDER_PURPOSE_OPTIONS = [
  { value: "po_from_dealer", label: "Dealer PO" },
  { value: "delivery_photo", label: "Delivery Photo" },
  { value: "signed_dn", label: "Signed Delivery Note" },
  { value: "supporting_doc", label: "Supporting Doc" },
];

export const Route = createFileRoute("/_app/sales/orders")({
  component: SalesOrdersPage,
});

const NOW = Date.now();

type TabValue = "all" | SalesOrderStatus;

const TAB_OPTIONS: { value: TabValue; label: string }[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "picking", label: "Picking" },
  { value: "packed", label: "Packed" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

const OPEN_STATUSES: SalesOrderStatus[] = [
  "submitted",
  "approved",
  "picking",
  "packed",
  "shipped",
];

function formatDateShort(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function SalesOrdersPage() {
  const isMock = useMockData();
  // Sales need order totals to do their job; warehouse only handles
  // operational fields (qty, status). Hide prices from warehouse.
  const role = useCurrentRole();
  const hidePrices = role === "warehouse";

  const [rows, setRows] = usePersistentMockState<MockSalesOrder[]>(
    "salesOrders",
    isMock ? mockSalesOrders : [],
  );
  const [dealers] = usePersistentMockState<MockDealer[]>("dealers", mockDealers);
  const [dealerTiers] = usePersistentMockState<MockDealerTier[]>(
    "dealerTiers",
    mockDealerTiers,
  );
  const [products] = usePersistentMockState<MockProduct[]>(
    "products",
    mockProducts,
  );
  const [taxRates] = usePersistentMockState<MockTaxRate[]>(
    "taxRates",
    mockTaxRates,
  );
  const [branches] = usePersistentMockState<typeof mockBranches>(
    "branches",
    mockBranches,
  );
  const [tab, setTab] = useState<TabValue>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockSalesOrder | null>(null);
  const [editingOrder, setEditingOrder] = useState<MockSalesOrder | null>(null);

  /* ── KPIs ── */
  const kpis = useMemo(() => {
    const monthStart = new Date(
      new Date(NOW).getFullYear(),
      new Date(NOW).getMonth(),
      1,
    ).getTime();
    let revenueMtd = 0n;
    let openCount = 0;
    let awaiting = 0;
    let deliveredTotal = 0n;
    let deliveredCount = 0;
    for (const r of rows) {
      if (r.status === "delivered" && r.deliveredAt && r.deliveredAt >= monthStart) {
        revenueMtd += r.totalInBaseCents;
      }
      if ((OPEN_STATUSES as string[]).includes(r.status)) openCount += 1;
      if (r.status === "submitted") awaiting += 1;
      if (r.status === "delivered") {
        deliveredTotal += r.totalInBaseCents;
        deliveredCount += 1;
      }
    }
    const avgOrder =
      deliveredCount === 0 ? 0n : deliveredTotal / BigInt(deliveredCount);
    return { revenueMtd, openCount, awaiting, avgOrder };
  }, [rows]);

  /* ── Filtering ── */
  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    return rows.filter((r) => r.status === tab);
  }, [rows, tab]);

  /* ── Lookup maps ── */
  const dealersById = useMemo(
    () => new Map(dealers.map((d) => [d._id, d])),
    [dealers],
  );

  const productsById = useMemo(
    () => new Map(products.map((p) => [p._id, p])),
    [products],
  );

  /* ── Live invoices (read-only here) used to compute dealer credit usage.
   *    The invoices route owns the writes; we just hydrate the same key so
   *    edits made there are reflected when the order form opens. ── */
  const [invoicesForCredit] = usePersistentMockState<MockInvoice[]>(
    "invoices",
    mockInvoices,
  );

  /* ── getDealerCredit callback passed to SalesOrderForm — returns the
   *    dealer's limit + live A/R outstanding in BDT base cents. ── */
  const getDealerCredit = useMemo(() => {
    return (dealerId: string) => {
      const dealer = dealersById.get(dealerId);
      if (!dealer) return null;
      return {
        limitBaseCents: dealer.creditLimitCents,
        outstandingBaseCents: dealerOutstandingBaseCents(
          dealerId,
          invoicesForCredit,
        ),
      };
    };
  }, [dealersById, invoicesForCredit]);

  /* ── Form prop data (memoized) ── */
  const dealerOptions = useMemo(
    () =>
      dealers.map((d) => {
        const tier = dealerTiers.find((t) => t._id === d.tierId);
        return {
          id: d._id,
          displayName: d.displayName,
          tierLabel: tier?.displayName,
        };
      }),
    [dealers, dealerTiers],
  );
  const branchOptions = useMemo(
    () => branches.map((b) => ({ id: b._id, displayName: b.displayName })),
    [branches],
  );
  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        id: p._id,
        displayName: p.displayName,
        defaultPriceCents: BigInt(
          Math.round(Number(p.standardCostCents) * 1.6),
        ),
        defaultTaxRateId: p.defaultTaxRateId,
      })),
    [products],
  );
  const taxRateOptions = useMemo(
    () =>
      taxRates.map((t) => ({
        id: t._id,
        displayName: t.displayName,
        ratePercentE4: t.ratePercentE4,
      })),
    [taxRates],
  );

  /* ── Lines per order count (used in table) ── */
  const lineCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r._id, linesForOrder(r._id).length);
    return counts;
  }, [rows]);

  /* ── Mutation handler is defined later via useCallback-like closure;
   *    since columns reference it, capture a stable ref. */
  const handleActionRef = React.useRef<
    (order: MockSalesOrder, action: OrderAction) => void
  >(() => {});

  /* ── Columns ── */
  const columns = useMemo<ColumnDef<MockSalesOrder>[]>(
    () => {
      const cols: ColumnDef<MockSalesOrder>[] = [
        {
          accessorKey: "orderNumber",
          header: "Order #",
          cell: ({ row }) => (
            <span className="font-mono text-[12px] text-(--text-primary)">
              {row.original.orderNumber}
            </span>
          ),
          meta: { mono: true },
        },
        {
          id: "dealer",
          header: "Dealer",
          cell: ({ row }) => (
            <span className="text-(--text-primary)">
              {dealersById.get(row.original.dealerId)?.displayName ?? "—"}
            </span>
          ),
        },
        {
          accessorKey: "placedAt",
          header: "Placed",
          cell: ({ row }) => (
            <span
              className="text-(--text-secondary) tabular-nums"
              title={formatDateShort(row.original.placedAt)}
            >
              {formatRelative(row.original.placedAt)}
            </span>
          ),
          meta: { hideOnMobile: true },
        },
        {
          id: "items",
          header: "Items",
          cell: ({ row }) => (
            <Badge variant="neutral">
              {lineCounts.get(row.original._id) ?? 0}
            </Badge>
          ),
          meta: { numeric: true },
        },
      ];

      if (!hidePrices) {
        cols.push({
          id: "total",
          header: "Total",
          cell: ({ row }) => (
            <span className="font-mono tabular-nums">
              {formatMoney(row.original.totalInBaseCents, "BDT", {
                locale: "en-BD",
              })}
            </span>
          ),
          meta: { numeric: true, mono: true },
        });
      }

      cols.push(
        {
          id: "status",
          header: "Status",
          cell: ({ row }) => (
            <SalesOrderStatusBadge status={row.original.status} />
          ),
        },
        {
          accessorKey: "updatedAt",
          header: "Updated",
          cell: ({ row }) => (
            <span
              className="text-(--text-secondary) tabular-nums"
              title={formatDateShort(row.original.updatedAt)}
            >
              {formatRelative(row.original.updatedAt)}
            </span>
          ),
          meta: { hideOnMobile: true },
        },
        {
          id: "rowActions",
          header: () => <span className="sr-only">Actions</span>,
          // Right-aligned chips, context-aware confirm popovers.
          cell: ({ row }) => (
            <OrderRowActions
              status={row.original.status}
              orderNumber={row.original.orderNumber}
              lineCount={lineCounts.get(row.original._id) ?? 0}
              onAction={(action) =>
                handleActionRef.current(row.original, action)
              }
            />
          ),
          // Don't open the row detail when clicking inside this cell.
          meta: { numeric: true },
          enableSorting: false,
        },
      );

      return cols;
    },
    [dealersById, lineCounts, hidePrices],
  );

  /* ── Mutations (mock-only writes for now). ── */
  function handleCreate(values: SalesOrderFormValues) {
    if (!isMock) return;
    const now = Date.now();
    const yearOrders = rows.length + 1;
    const orderNumber = `ORD-2026-${String(yearOrders).padStart(6, "0")}`;
    let amount = 0n;
    let tax = 0n;
    for (const l of values.lines) {
      const qty = BigInt(Math.max(0, Math.round(l.quantity)));
      const sub = qty * l.unitPriceCents;
      const taxRate = taxRates.find((t) => t._id === l.taxRateId);
      const ratePercentE4 = taxRate?.ratePercentE4 ?? 0n;
      tax += (sub * ratePercentE4) / 1_000_000n;
      amount += sub;
    }
    const total = amount + tax;
    const newRow: MockSalesOrder = {
      _id: `so_local_${now}`,
      orderNumber,
      dealerId: values.dealerId,
      branchId: values.branchId,
      placedByUserId: "dev-user",
      status: "draft",
      placedAt: now,
      approvedAt: null,
      approvedByUserId: null,
      shippedAt: null,
      deliveredAt: null,
      requestedDeliveryDate: values.requestedDeliveryDate ?? null,
      currency: "BDT",
      fxRateToBaseE8: 100_000_000n,
      amountCents: amount,
      discountInBaseCents: 0n,
      taxInBaseCents: tax,
      totalInBaseCents: total,
      pointsEarned: 0n,
      pointsRedeemed: 0n,
      notes: values.notes || null,
      createdAt: now,
      updatedAt: now,
    };
    setRows((prev) => [newRow, ...prev]);
    setCreateOpen(false);
    toast.success(`Draft ${orderNumber} created (mock)`);
  }

  function handleEditOrder(values: SalesOrderFormValues) {
    if (!isMock || !editingOrder) return;
    if (editingOrder.status !== "draft") {
      toast.error("Only draft orders can be edited.");
      return;
    }
    const now = Date.now();
    let amount = 0n;
    let tax = 0n;
    for (const l of values.lines) {
      const qty = BigInt(Math.max(0, Math.round(l.quantity)));
      const sub = qty * l.unitPriceCents;
      const taxRate = taxRates.find((t) => t._id === l.taxRateId);
      const ratePercentE4 = taxRate?.ratePercentE4 ?? 0n;
      tax += (sub * ratePercentE4) / 1_000_000n;
      amount += sub;
    }
    const total = amount + tax;
    setRows((prev) =>
      prev.map((r) =>
        r._id === editingOrder._id
          ? {
              ...r,
              dealerId: values.dealerId,
              branchId: values.branchId,
              requestedDeliveryDate: values.requestedDeliveryDate ?? null,
              amountCents: amount,
              taxInBaseCents: tax,
              totalInBaseCents: total,
              notes: values.notes || null,
              updatedAt: now,
            }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev && prev._id === editingOrder._id
        ? {
            ...prev,
            dealerId: values.dealerId,
            branchId: values.branchId,
            requestedDeliveryDate: values.requestedDeliveryDate ?? null,
            amountCents: amount,
            taxInBaseCents: tax,
            totalInBaseCents: total,
            notes: values.notes || null,
            updatedAt: now,
          }
        : prev,
    );
    setEditingOrder(null);
    toast.success(
      `Updated ${editingOrder.orderNumber}. Audit trail recorded.`,
    );
  }

  function handleAction(
    order: MockSalesOrder,
    action: OrderAction,
    payload?: { reason?: string },
  ) {
    if (!isMock) {
      toast.info(`Wired through InstantDB in real mode (${action})`);
      return;
    }
    const transitionMap: Record<OrderAction, SalesOrderStatus> = {
      submit: "submitted",
      approve: "approved",
      reject: "rejected",
      request_modification: "draft",
      start_picking: "picking",
      mark_packed: "packed",
      mark_shipped: "shipped",
      mark_delivered: "delivered",
      cancel: "cancelled",
    };
    const next = transitionMap[action];
    // request_modification takes submitted → draft, which is NOT in the
    // forward-only transitions table; allow it explicitly.
    if (
      action !== "request_modification" &&
      !canTransitionSalesOrder(order.status, next)
    ) {
      toast.error(`Cannot ${action} from ${order.status}`);
      return;
    }
    const now = Date.now();
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== order._id) return r;
        const update: Partial<MockSalesOrder> = {
          status: next,
          updatedAt: now,
        };
        if (next === "approved") {
          update.approvedAt = now;
          update.approvedByUserId = "dev-user";
        }
        if (next === "shipped") update.shippedAt = now;
        if (next === "delivered") update.deliveredAt = now;
        if (next === "draft" && action === "request_modification") {
          update.approvedAt = null;
          update.approvedByUserId = null;
        }
        return { ...r, ...update };
      }),
    );
    if (detailRow && detailRow._id === order._id) {
      setDetailRow({
        ...order,
        status: next,
        updatedAt: now,
      });
    }
    const reasonNote = payload?.reason ? ` — ${payload.reason}` : "";
    toast.success(`${order.orderNumber} → ${next}${reasonNote}`);
  }

  // Keep the ref pointing at the latest closure so in-row chips always
  // dispatch with current `rows` / `detailRow` state.
  handleActionRef.current = (order, action) => handleAction(order, action);

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockSalesOrder>[] = [
      { key: "orderNumber", label: "Order #" },
      {
        key: "dealerId",
        label: "Dealer",
        value: (r) => dealersById.get(r.dealerId)?.displayName ?? r.dealerId,
      },
      {
        key: "placedAt",
        label: "Date",
        format: (v) => formatDateForCsv(Number(v)),
      },
      { key: "status", label: "Status" },
      {
        key: "_id",
        label: "Items",
        value: (r) => lineCounts.get(r._id) ?? 0,
      },
      // Money: BigInt cents → default formatter writes "142.50".
      { key: "totalInBaseCents", label: "Total BDT" },
      { key: "currency", label: "Currency" },
      {
        key: "updatedAt",
        label: "Updated",
        format: (v) => formatDateForCsv(Number(v)),
      },
    ];
    downloadCsv(
      exportFilename("sales-orders", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Sales Orders"
        description="The full sales order pipeline — drafts, approvals, picking, shipping, and delivery."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New order
            </Button>
          </>
        }
      />

      {/* KPI strip */}
      <section
        className={
          hidePrices
            ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
            : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        }
        aria-label="Sales order summary"
      >
        {!hidePrices ? (
          <KpiCard
            caption="Revenue MTD"
            value={formatMoneySmart(kpis.revenueMtd, "BDT", { locale: "en-BD" })}
            hint="Delivered orders this month"
          />
        ) : null}
        <KpiCard
          caption="Open orders"
          value={kpis.openCount}
          hint="In-flight (submitted → shipped)"
        />
        <KpiCard
          caption="Awaiting approval"
          value={kpis.awaiting}
          hint="Status: submitted"
        />
        {!hidePrices ? (
          <KpiCard
            caption="Avg order value"
            value={formatMoneySmart(kpis.avgOrder, "BDT", { locale: "en-BD" })}
            hint="Across delivered orders"
          />
        ) : null}
      </section>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
        <TabsList>
          {TAB_OPTIONS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No orders match"
        emptyDescription={
          rows.length === 0
            ? "Dealers can submit orders from the dealer portal — they will land here for approval."
            : "Try a different tab."
        }
        emptyIcon={<Receipt className="h-5 w-5" aria-hidden />}
        emptyAction={
          rows.length === 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New order
            </Button>
          ) : undefined
        }
      />

      {/* Create */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>New sales order</SheetTitle>
            <SheetDescription>
              Pick a dealer and add at least one line. Drafts can be edited
              freely until they're submitted for approval.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <SalesOrderForm
              dealers={dealerOptions}
              branches={branchOptions}
              products={productOptions}
              taxRates={taxRateOptions}
              resolveOffer={(dealerId, productId) => {
                const offer = offersForDealer(dealerId).find(
                  (o) => o.productId === productId && o.isActive,
                );
                return offer
                  ? { unitPriceCents: offer.unitPriceCents }
                  : null;
              }}
              getDealerCredit={getDealerCredit}
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRow(null);
            setEditingOrder(null);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow && editingOrder ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingOrder.orderNumber}</SheetTitle>
                <SheetDescription>
                  Drafts can be edited freely; once submitted edits are locked.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <SalesOrderForm
                  submitLabel="Save changes"
                  dealers={dealerOptions}
                  branches={branchOptions}
                  products={productOptions}
                  taxRates={taxRateOptions}
                  resolveOffer={(dealerId, productId) => {
                    const offer = offersForDealer(dealerId).find(
                      (o) => o.productId === productId && o.isActive,
                    );
                    return offer
                      ? { unitPriceCents: offer.unitPriceCents }
                      : null;
                  }}
                  getDealerCredit={getDealerCredit}
                  initialValues={{
                    dealerId: editingOrder.dealerId,
                    branchId: editingOrder.branchId,
                    requestedDeliveryDate: editingOrder.requestedDeliveryDate,
                    notes: editingOrder.notes ?? "",
                    lines: linesForOrder(editingOrder._id).map((l) => ({
                      productId: l.productId,
                      variantId: l.variantId,
                      quantity: l.quantityOrdered,
                      unitPriceCents: l.unitPriceCents,
                      currency: "BDT",
                      taxRateId: l.taxRateId,
                    })),
                  }}
                  onSubmit={handleEditOrder}
                  onCancel={() => setEditingOrder(null)}
                />
              </SheetBody>
            </>
          ) : detailRow ? (
            <SalesOrderDetail
              row={detailRow}
              onAction={(action, payload) =>
                handleAction(detailRow, action, payload)
              }
              onEdit={() => setEditingOrder(detailRow)}
              productsById={productsById}
              hidePrices={hidePrices}
              dealerTiers={dealerTiers}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail Sheet
 * ───────────────────────────────────────────────────────────── */

function SalesOrderDetail({
  row,
  onAction,
  onEdit,
  productsById,
  hidePrices,
  dealerTiers,
}: {
  row: MockSalesOrder;
  onAction: (action: OrderAction, payload?: { reason?: string }) => void;
  onEdit: () => void;
  productsById: Map<string, { displayName: string }>;
  hidePrices: boolean;
  dealerTiers: MockDealerTier[];
}) {
  const dealer = findDealer(row.dealerId);
  const tier = useMemo(
    () =>
      dealer ? (dealerTiers.find((t) => t._id === dealer.tierId) ?? null) : null,
    [dealer, dealerTiers],
  );
  const lines = linesForOrder(row._id);
  const history = historyForOrder(row._id);
  const canEdit = row.status === "draft";

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{row.orderNumber}</span>
          <SalesOrderStatusBadge status={row.status} />
          <RecordAuditBadge tableName="salesOrders" recordId={row._id} />
          <AttachmentBadge
            parentTable="salesOrders"
            parentRecordId={row._id}
          />
        </SheetTitle>
        <SheetDescription>
          {dealer?.displayName ?? "—"} · placed {formatRelative(row.placedAt)}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
          {/* Header summary */}
          <section className="grid grid-cols-2 gap-3">
            {!hidePrices ? (
              <>
                <DetailField
                  label="Total"
                  value={formatMoney(row.totalInBaseCents, "BDT", {
                    locale: "en-BD",
                  })}
                />
                <DetailField
                  label="Tax"
                  value={formatMoney(row.taxInBaseCents, "BDT", {
                    locale: "en-BD",
                  })}
                />
                <DetailField
                  label="Discount"
                  value={formatMoney(row.discountInBaseCents, "BDT", {
                    locale: "en-BD",
                  })}
                />
              </>
            ) : null}
            <DetailField
              label="Currency"
              value={
                <Badge variant="neutral" className="font-mono">
                  {row.currency}
                </Badge>
              }
            />
            <DetailField
              label="Placed"
              value={formatDateShort(row.placedAt)}
            />
            <DetailField
              label="Requested delivery"
              value={
                row.requestedDeliveryDate
                  ? formatDateShort(row.requestedDeliveryDate)
                  : "—"
              }
            />
          </section>

          {/* Dealer card */}
          {dealer ? (
            <section className="rounded-[var(--radius-md)] border border-(--border-subtle) p-3 flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium text-(--text-primary)">
                  {dealer.displayName}
                </span>
                {tier ? <Badge variant="neutral">{tier.displayName}</Badge> : null}
              </div>
              <div className="text-[12px] text-(--text-secondary) flex flex-wrap gap-x-4 gap-y-0.5">
                <span className="font-mono">{dealer.code}</span>
                <span>{dealer.contactJson.contactPerson ?? "—"}</span>
                <span>{dealer.contactJson.phone}</span>
                {dealer.contactJson.email ? (
                  <span>{dealer.contactJson.email}</span>
                ) : null}
              </div>
              <div className="text-[12px] text-(--text-tertiary)">
                {dealer.addressJson.line1}
                {dealer.addressJson.city
                  ? `, ${dealer.addressJson.city}`
                  : ""}
              </div>
            </section>
          ) : null}

          {/* Lines */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Lines ({lines.length})
            </div>
            {lines.length === 0 ? (
              <div className="text-[12px] text-(--text-tertiary)">
                No line data.
              </div>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-(--text-tertiary) border-b border-(--border-subtle)">
                    <th className="py-2">Item</th>
                    <th className="text-right">Qty</th>
                    {!hidePrices ? (
                      <>
                        <th className="text-right">Unit</th>
                        <th className="text-right">Total (BDT)</th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <LineRow
                      key={l._id}
                      line={l}
                      productsById={productsById}
                      hidePrices={hidePrices}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Timeline */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Status timeline
            </div>
            <StatusTimeline history={history} />
          </section>

          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {row.notes}
              </p>
            </section>
          ) : null}
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="salesOrders" recordId={row._id} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <AttachmentList
              parentTable="salesOrders"
              parentRecordId={row._id}
              purposeOptions={SALES_ORDER_PURPOSE_OPTIONS}
              allowCustomPurpose
              mimeAllowlist={["image/*", "application/pdf"]}
              title="Drop order docs here"
              description="Dealer PO, delivery photos, signed delivery notes."
            />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        {canEdit ? (
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
            <Pencil className="size-4" />
          </Button>
        ) : (
          <Tooltip content="Cannot edit closed records — create a credit note instead">
            <span>
              <Button variant="ghost" size="icon" disabled aria-label="Edit">
                <Pencil className="size-4" />
              </Button>
            </span>
          </Tooltip>
        )}
        <OrderActionsBar status={row.status} onAction={onAction} />
      </SheetFooter>
    </>
  );
}

function LineRow({
  line,
  productsById,
  hidePrices,
}: {
  line: MockSalesOrderLine;
  productsById: Map<string, { displayName: string }>;
  hidePrices: boolean;
}) {
  const product = productsById.get(line.productId);
  return (
    <tr className="border-b border-(--border-subtle)">
      <td className="py-2 text-(--text-primary)">
        {product?.displayName ?? line.productId}
      </td>
      <td className="text-right tabular-nums">{line.quantityOrdered}</td>
      {!hidePrices ? (
        <>
          <td className="text-right font-mono tabular-nums">
            {formatMoney(line.unitPriceCents, "BDT", { locale: "en-BD" })}
          </td>
          <td className="text-right font-mono tabular-nums">
            {formatMoney(line.lineTotalInBaseCents, "BDT", { locale: "en-BD" })}
          </td>
        </>
      ) : null}
    </tr>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}
